import React, { useState, useRef } from 'react';
import { PageTitle } from '../components/PageTitle';
import { MailIcon, LinkedinIcon, SendIcon } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet';
export function Contact() {
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    // Initialize EmailJS with your public key
    emailjs.init('meVpLvNyXbOnnQevs'); // Replace with your actual EmailJS public key
    // Prepare template parameters
    const templateParams = {
      to_email: 'katalinabock@gmail.com',
      from_name: formData.name,
      from_email: formData.email,
      subject: formData.subject,
      message: formData.message
    };
    // Send email using EmailJS
    emailjs.send('service_arw57bp',
    // Replace with your EmailJS service ID
    'template_f4h8eim',
    // Replace with your EmailJS template ID
    templateParams).then(response => {
      console.log('Email sent successfully!', response.status, response.text);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }).catch(err => {
      console.error('Failed to send email:', err);
      setIsSubmitting(false);
      setSubmitError(true);
      setErrorMessage('Failed to send your message. Please try again or contact directly via email.');
    });
  };
  return <div data-id="element-1247">
      <PageTitle title="Contact Me" canonical="/contact" description="Get in touch to discuss product management opportunities, innovation strategies, or potential collaborations." keywords="contact Katalina Bock, product management opportunities, innovation strategies, collaboration, product leader contact" data-id="element-1248" />
      <Helmet data-id="element-1249">
        <script type="application/ld+json" data-id="element-1250">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Katalina Bock',
          description: 'Get in touch to discuss product management opportunities, innovation strategies, or potential collaborations.',
          mainEntity: {
            '@type': 'Person',
            name: 'Katalina Bock',
            email: 'katalinabock@gmail.com',
            sameAs: ['https://www.linkedin.com/in/katalinabock']
          }
        })}
        </script>
      </Helmet>
      <section className="bg-gradient-to-r from-[#E0F5F8] to-[#F0FAFC] text-gray-900 py-20" data-id="element-1251">
        <div className="container mx-auto px-4" data-id="element-1252">
          <h1 className="text-4xl font-bold mb-4 text-center" data-id="element-1253">Let's Connect</h1>
          <p className="text-xl mb-6 text-gray-700 text-center max-w-2xl mx-auto" data-id="element-1254">
            I'm always open to discussing product management opportunities,
            innovation strategies, or potential collaborations.
          </p>
        </div>
      </section>
      <section className="py-16 bg-white" data-id="element-1255">
        <div className="container mx-auto px-4" data-id="element-1256">
          <div className="flex flex-col md:flex-row gap-12 max-w-5xl mx-auto" data-id="element-1257">
            <div className="md:w-1/3" data-id="element-1258">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6" data-id="element-1259">
                Contact Information
              </h2>
              <div className="space-y-6" data-id="element-1260">
                <div className="flex items-start" data-id="element-1261">
                  <div className="bg-[#E0F5F8] p-3 rounded-full mr-4" data-id="element-1262">
                    <MailIcon size={20} className="text-[#0096B4]" data-id="element-1263" />
                  </div>
                  <div data-id="element-1264">
                    <h3 className="font-medium text-gray-900" data-id="element-1265">Email</h3>
                    <a href="mailto:katalinabock@gmail.com" className="text-[#0096B4] hover:text-[#00778F]" data-id="element-1266">
                      katalinabock@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1267">
                  <div className="bg-[#E0F5F8] p-3 rounded-full mr-4" data-id="element-1268">
                    <LinkedinIcon size={20} className="text-[#0096B4]" data-id="element-1269" />
                  </div>
                  <div data-id="element-1270">
                    <h3 className="font-medium text-gray-900" data-id="element-1271">LinkedIn</h3>
                    <a href="https://www.linkedin.com/in/katalinabock" target="_blank" rel="noopener noreferrer" className="text-[#0096B4] hover:text-[#00778F]" data-id="element-1272">
                      linkedin.com/in/katalinabock
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-2/3" data-id="element-1273">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6" data-id="element-1274">
                Send Me a Message
              </h2>
              {submitSuccess ? <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center" data-id="element-1275">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded-full mb-4" data-id="element-1276">
                    <SendIcon size={24} data-id="element-1277" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-1278">
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1279">
                    Thank you for reaching out. I'll get back to you as soon as
                    possible.
                  </p>
                  <button onClick={() => setSubmitSuccess(false)} className="bg-[#0096B4] hover:bg-[#00778F] text-white px-6 py-2 rounded-md font-medium transition-colors duration-200" data-id="element-1280">
                    Send Another Message
                  </button>
                </div> : <form ref={form} onSubmit={handleSubmit} className="space-y-6" data-id="element-1281">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1282">
                    <div data-id="element-1283">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1284">
                        Name
                      </label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0096B4] focus:border-transparent" data-id="element-1285" />
                    </div>
                    <div data-id="element-1286">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1287">
                        Email
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0096B4] focus:border-transparent" data-id="element-1288" />
                    </div>
                  </div>
                  <div data-id="element-1289">
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1290">
                      Subject
                    </label>
                    <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0096B4] focus:border-transparent" data-id="element-1291" />
                  </div>
                  <div data-id="element-1292">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1293">
                      Message
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0096B4] focus:border-transparent" data-id="element-1294"></textarea>
                  </div>
                  {submitError && <div className="text-red-500 text-sm" data-id="element-1295">{errorMessage}</div>}
                  <button type="submit" disabled={isSubmitting} className={`w-full bg-[#0096B4] hover:bg-[#00778F] text-white px-6 py-3 rounded-md font-medium transition-colors duration-200 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1296">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1297">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1298"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1299"></path>
                        </svg>
                        Sending...
                      </> : 'Send Message'}
                  </button>
                </form>}
            </div>
          </div>
        </div>
      </section>
    </div>;
}